import {
  PREVIEW_QUEUE_MAX_FRAMES,
  type PreviewPartKind,
  PreviewPartParser,
  type WireError,
} from "./protocol";
import { BRIDGE_ERROR_CODES, BridgeError } from "./types";

export interface PumpedPart {
  kind: PreviewPartKind;
  bytes: Uint8Array<ArrayBuffer>;
}

/** false when `code` is not one of ours, so an unrecognized code reads as a bridge fault. */
export function isWireError(body: unknown): body is WireError {
  return (
    typeof body === "object" &&
    body !== null &&
    "code" in body &&
    "message" in body &&
    (BRIDGE_ERROR_CODES as readonly unknown[]).includes((body as { code: unknown }).code) &&
    typeof (body as { message: unknown }).message === "string" &&
    ["string", "undefined"].includes(typeof (body as { detail?: unknown }).detail)
  );
}

function decodeStreamError(bytes: Uint8Array): BridgeError {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    parsed = undefined;
  }
  return isWireError(parsed)
    ? new BridgeError(parsed.code, parsed.message, parsed.detail)
    : new BridgeError("PreviewLost", "preview ended with an unreadable error");
}

/** The queue-wait is intentionally unbounded here: the caller bounds every `next()`
 *  itself, so the first part can time out as a missing hello rather than a frame. */
export function pumpPreview(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  path: string,
  isClosing: () => boolean,
): AsyncIterable<PumpedPart> {
  type QueueItem =
    | { kind: "frame" | "hello"; bytes: Uint8Array<ArrayBuffer> }
    | { kind: "error"; error: BridgeError }
    | { kind: "done" };

  const queue: QueueItem[] = [];
  let wake: (() => void) | null = null;

  function push(item: QueueItem): void {
    // Frames are bounded and the oldest go first — preview is live, so a stale frame is
    // worth less than the memory it holds. Errors and the done marker are never dropped.
    if (item.kind === "frame") {
      const queued = queue.reduce((n, q) => (q.kind === "frame" ? n + 1 : n), 0);
      if (queued >= PREVIEW_QUEUE_MAX_FRAMES) {
        queue.splice(
          queue.findIndex((q) => q.kind === "frame"),
          1,
        );
      }
    }
    queue.push(item);
    if (wake) {
      const w = wake;
      wake = null;
      w();
    }
  }

  const parser = new PreviewPartParser();
  void (async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          push(
            parser.pendingBytes > 0
              ? {
                  kind: "error",
                  error: new BridgeError("PreviewLost", `${path} ended mid-part`),
                }
              : { kind: "done" },
          );
          return;
        }
        for (const part of parser.push(value)) {
          if (part.kind === "error") {
            push({ kind: "error", error: decodeStreamError(part.bytes) });
          } else {
            push({ kind: part.kind, bytes: part.bytes });
          }
        }
      }
    } catch (err) {
      push(
        isClosing()
          ? { kind: "done" }
          : {
              kind: "error",
              error: err instanceof BridgeError ? err : new BridgeError("PreviewLost", String(err)),
            },
      );
    }
  })();

  return {
    async *[Symbol.asyncIterator]() {
      try {
        while (true) {
          if (queue.length === 0) {
            await new Promise<void>((resolve) => {
              wake = resolve;
            });
          }
          const item = queue.shift();
          if (!item) continue;
          if (item.kind === "done") return;
          if (item.kind === "error") throw item.error;
          yield item;
        }
      } finally {
        await reader.cancel().catch(() => {});
      }
    },
  };
}
